import { useMutation } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { useFormContext } from 'react-hook-form';

import type {
  ConfirmQualificationInput,
  MonthYear,
  Qualification,
  UpdateQualificationInput,
  Year,
} from 'src/__generated__/graphql';
import type { ResumeFieldsValues } from 'src/components/ResumeFields/ResumeFields';
import { useConfig } from 'src/context/config';

import {
  CONFIRM_MUTATION,
  DELETE_MUTATION,
  REJECT_MUTATION,
  UPDATE_MUTATION,
} from '../data';

type QualificationValues = Pick<
  Qualification,
  'id' | 'completed' | 'completionDate' | 'highlights' | 'institute' | 'name'
>;
function isYear(
  value: Year | MonthYear | number | null | undefined,
): value is Year {
  return value !== null && typeof value === 'object' && 'year' in value;
}

export function parseValues(
  values: QualificationValues,
): ConfirmQualificationInput | UpdateQualificationInput {
  const completionYear =
    values.completed && isYear(values.completionDate)
      ? values.completionDate.year
      : (values.completionDate as unknown as number);
  return {
    completed: values.completed,
    completionYear: values.completed ? completionYear : null,
    expectedCompletionDate: !values.completed
      ? (values.completionDate as MonthYear)
      : null,
    highlights: values.highlights,
    id: values.id,
    institute: values.institute,
    name: values.name,
  };
}

export function useEducationMutations() {
  const { config } = useConfig();
  const { language } = useMelwaysInfo();
  const [remove, stateRemove] = useMutation(DELETE_MUTATION);
  const [reject, stateReject] = useMutation(REJECT_MUTATION);
  const [confirm, stateConfirm] = useMutation(CONFIRM_MUTATION);
  const [update, stateUpdate] = useMutation(UPDATE_MUTATION);

  // Get resumeId from the form
  const { watch } = useFormContext<ResumeFieldsValues>();
  const resumeId = watch('resume-select');

  const resetAllErrors = () => {
    stateRemove.reset();
    stateReject.reset();
    stateConfirm.reset();
    stateUpdate.reset();
  };

  const deleteQualification = (id: string) =>
    remove({
      variables: {
        id,
        zone: config.ZONE,
        languageCode: language,
      },
    });

  const rejectQualification = (id: string) =>
    reject({
      variables: {
        id,
        zone: config.ZONE,
        languageCode: language,
        contextId: resumeId,
      },
    });

  const confirmQualification = (values: QualificationValues) =>
    confirm({
      variables: {
        qualification: parseValues(values),
        zone: config.ZONE,
        languageCode: language,
        contextId: resumeId,
      },
    });

  const updateQualification = (id: string, values: QualificationValues) =>
    update({
      variables: {
        input: parseValues({ ...values, id }),
        zone: config.ZONE,
        languageCode: language,
      },
    });

  return {
    deleteQualification,
    rejectQualification,
    confirmQualification,
    updateQualification,
    error:
      stateRemove.error ||
      stateReject.error ||
      stateConfirm.error ||
      stateUpdate.error,
    reset: resetAllErrors,
  };
}
